// Data processing utilities for The Knight Client AI

const COST_PER_MESSAGE = 10;

export const processClientData = (revenueData, complianceData, workloadData, npsData, costPerHour) => {
  if (revenueData.length === 0) return [];

  // Calculate medians for persona classification
  const validRevenues = revenueData.map(d => d.totalRevenue).filter(r => r > 0).sort((a, b) => a - b);
  const validMessages = workloadData.map(d => d.messagesReceived).filter(m => m > 0).sort((a, b) => a - b);
  
  const medianRevenue = validRevenues[Math.floor(validRevenues.length / 2)] || 0;
  const topQuartileRevenue = validRevenues[Math.floor(validRevenues.length * 0.75)] || 0;
  const medianMessages = validMessages[Math.floor(validMessages.length / 2)] || 0;

  return revenueData.map(rev => {
    const planId = rev.planNumber;
    
    const comp = complianceData.find(d => d.planNumber === planId);
    const work = workloadData.find(d => d.planNumber === planId);
    
    const npsEntries = npsData.filter(d => d.planNumber === planId);
    let npsScore = null;
    if (npsEntries.length > 0) {
      const sum = npsEntries.reduce((acc, curr) => acc + curr.score, 0);
      npsScore = parseFloat((sum / npsEntries.length).toFixed(1));
    }

    const manager = comp?.manager || npsEntries[0]?.manager || 'Unknown';
    const teamLeader = npsEntries[0]?.teamLeader || 'Unknown';

    const revenue = rev.totalRevenue;
    const managementFees = rev.managementFees;
    
    const messagesReceived = work?.messagesReceived || 0;
    const messageCost = messagesReceived * COST_PER_MESSAGE;
    
    const totalSeconds = work ? (work.newConversations * work.handleTimeAvg) : 0;
    const hours = totalSeconds / 3600;
    const handleCost = hours * costPerHour;
    
    const workloadCost = messageCost + handleCost;
    const revWorkloadVariance = revenue - workloadCost;
    const margin = revenue > 0 ? revWorkloadVariance / revenue : 0;

    let complianceScore = 100;
    if (comp) {
      const penalties = (comp.overdueCount * 20) + (comp.warningCount * 5);
      complianceScore = Math.max(0, 100 - penalties);
    } else {
      complianceScore = 50; 
    }
    
    // Persona classification
    let persona = 'Unknown';
    const isProfitable = revWorkloadVariance > 0;
    const isHighRevenue = revenue > medianRevenue;
    const isTopTierRevenue = revenue > topQuartileRevenue;
    const isHighMessageVolume = messagesReceived > medianMessages;

    if(isProfitable) {
      if(isTopTierRevenue && !isHighMessageVolume) {
        persona = 'Platinum Clients';
      } else if(isHighRevenue) {
        persona = 'Gold Clients';
      } else {
        persona = 'Growth Potential';
      }
    } else {
      if(isHighRevenue) {
        persona = 'Service Heavy';
      } else {
        persona = isHighMessageVolume ? 'Critical Review' : 'At Risk';
      }
    }

    let npsCategory = 'Unknown';
    if (npsScore !== null) {
      if (npsScore >= 9) npsCategory = 'Promoter';
      else if (npsScore >= 7) npsCategory = 'Passive';
      else npsCategory = 'Detractor';
    }

    return {
      id: planId,
      planName: planId,
      manager,
      teamLeader,
      managementFees,
      revenue,
      messageCost,
      handleCost,
      workloadCost,
      messagesReceived,
      revWorkloadVariance,
      margin,
      complianceScore,
      npsScore,
      npsCategory,
      persona
    };
  });
};

export const calculateMetrics = (mergedData, npsData) => {
  if (mergedData.length === 0) {
    return { 
      totalRevenue: 0, 
      totalMessageCost: 0, 
      totalHandleTimeCost: 0, 
      totalWorkloadCost: 0, 
      avgNPS: 0, 
      avgCompliance: 0, 
      totalPlans: 0 
    };
  }
  
  const totalRev = mergedData.reduce((acc, curr) => acc + curr.revenue, 0);
  const totalMsgCost = mergedData.reduce((acc, curr) => acc + curr.messageCost, 0);
  const totalHandleCost = mergedData.reduce((acc, curr) => acc + curr.handleCost, 0);
  const totalWorkload = totalMsgCost + totalHandleCost;

  const avgComp = mergedData.length ? mergedData.reduce((acc, curr) => acc + curr.complianceScore, 0) / mergedData.length : 0;

  let npsMetric = 0;
  const npsPlansWithScores = npsData.filter(nps => mergedData.some(m => m.id === nps.planNumber));
  if (npsPlansWithScores.length > 0) {
    const promoters = npsPlansWithScores.filter(d => d.score >= 9).length;
    const detractors = npsPlansWithScores.filter(d => d.score <= 6).length;
    npsMetric = ((promoters - detractors) / npsPlansWithScores.length) * 100;
  }

  return {
    totalRevenue: totalRev,
    totalMessageCost: totalMsgCost,
    totalHandleTimeCost: totalHandleCost,
    totalWorkloadCost: totalWorkload,
    avgNPS: npsMetric,
    avgCompliance: avgComp,
    totalPlans: mergedData.length
  };
};
